/**
 * SEO Configuration - Centralized metadata for all routes
 * 
 * This configuration provides:
 * - Unified SEO metadata management
 * - Type-safe route definitions
 * - Multi-language support
 * - Scalable structure for adding new pages
 * 
 * Best Practices:
 * - Keep titles under 60 characters for optimal display
 * - Keep descriptions between 150-160 characters
 * - Use action-oriented language in descriptions
 * - Include target keywords naturally
 * - Update this file when adding new routes
 * 
 * @important CANONICAL DOMAIN
 * All canonical URLs use https://finanzly.es (without www).
 * Ensure your server redirects www.finanzly.es → finanzly.es with 301 status.
 * This consolidates SEO authority to a single domain variant.
 */

import type { SupportedLanguage } from './i18n.config';

/**
 * Canonical domain - The primary domain used for all SEO purposes
 * @constant
 * @description All canonical URLs, sitemaps, and structured data should use this domain
 */
export const SEO_DOMAIN = 'https://finanzly.es';

/**
 * Legacy domain with www subdomain
 * @constant
 * @deprecated Use SEO_DOMAIN instead. This is kept for reference in legal documents.
 * @description Configure a 301 redirect from this domain to SEO_DOMAIN
 */
export const LEGACY_WWW_DOMAIN = 'https://www.finanzly.es';

/**
 * Canonical language - The primary language for SEO canonical URLs
 * @constant
 * @description All pages across all languages will have their canonical URL pointing
 * to this language version. This is the recommended strategy for multilingual sites
 * on a ccTLD domain to avoid content duplication issues.
 * 
 * Strategy: Spanish (es) is chosen as canonical because:
 * 1. Domain is finanzly.es (Spanish ccTLD)
 * 2. Provides clear signal to Google about primary content
 * 3. Other languages serve as alternates via hreflang
 * 4. Prevents "duplicate content" issues across language versions
 */
export const DEFAULT_CANONICAL_LANG = 'es' as const;

/**
 * Languages that should be actively indexed by search engines
 * @constant
 * @description Primary languages receive normal indexing signals.
 * Secondary languages use noindex meta tag initially to consolidate SEO authority,
 * but remain accessible to users and can be selectively indexed via hreflang.
 */
export const PRIMARY_INDEX_LANGUAGES = ['es', 'en'] as const;

// Default Open Graph image
export const DEFAULT_OG_IMAGE = `${SEO_DOMAIN}/images/brand/og-image.png`;

/**
 * SEO Metadata structure for each page
 */
export interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
}

/**
 * Multi-language SEO metadata
 * Note: We primarily support ES, EN, FR. DE and HI can be added when needed.
 */
export type MultiLangSEOMetadata = {
  es: SEOMetadata;
  en: SEOMetadata;
  fr: SEOMetadata;
  de?: SEOMetadata;  // Optional: German support
  hi?: SEOMetadata;  // Optional: Hindi support
};

/**
 * Route-based SEO configuration
 */
export interface RouteSEOConfig {
  path: string;
  metadata: MultiLangSEOMetadata;
}

/**
 * Main Pages SEO Configuration
 */
export const MAIN_PAGES_SEO: Record<string, MultiLangSEOMetadata> = {
  home: {
    es: {
      title: 'Finanzly - Herramientas Financieras y Calculadoras Gratis',
      description: 'Calculadoras financieras gratuitas para hipotecas, inversiones, ahorro y más. Toma decisiones inteligentes con nuestras herramientas y recursos educativos.',
      keywords: 'calculadora financiera, calculadora hipoteca, ahorro, inversión, finanzas personales',
      ogType: 'website',
    },
    en: {
      title: 'Finanzly - Free Financial Tools & Calculators',
      description: 'Free financial calculators for mortgages, investments, savings and more. Make smart decisions with our tools and educational resources.',
      keywords: 'financial calculator, mortgage calculator, savings, investment, personal finance',
      ogType: 'website',
    },
    fr: {
      title: 'Finanzly - Outils Financiers et Calculatrices Gratuits',
      description: 'Calculatrices financières gratuites pour hypothèques, investissements, épargne et plus. Prenez des décisions intelligentes avec nos outils.',
      keywords: 'calculatrice financière, calculatrice hypothèque, épargne, investissement, finances personnelles',
      ogType: 'website',
    },
  },
  
  tools: {
    es: {
      title: 'Herramientas Financieras - Calculadoras Gratis | Finanzly',
      description: 'Accede a nuestras calculadoras financieras: hipotecas, ahorro mensual, interés compuesto, inversiones y más. 100% gratis y fáciles de usar.',
      keywords: 'herramientas financieras, calculadoras gratis, calculadora hipoteca, calculadora ahorro',
      ogType: 'website',
    },
    en: {
      title: 'Financial Tools - Free Calculators | Finanzly',
      description: 'Access our financial calculators: mortgages, monthly savings, compound interest, investments and more. 100% free and easy to use.',
      keywords: 'financial tools, free calculators, mortgage calculator, savings calculator',
      ogType: 'website',
    },
    fr: {
      title: 'Outils Financiers - Calculatrices Gratuits | Finanzly',
      description: 'Accédez à nos calculatrices financières : hypothèques, épargne mensuelle, intérêts composés, investissements et plus. 100% gratuit.',
      keywords: 'outils financiers, calculatrices gratuits, calculatrice hypothèque, calculatrice épargne',
      ogType: 'website',
    },
  },
  
  resources: {
    es: {
      title: 'Recursos y Artículos de Finanzas Personales | Finanzly',
      description: 'Guías, artículos y recursos educativos sobre finanzas personales, inversión, ahorro e impuestos. Aprende a gestionar mejor tu dinero.',
      keywords: 'finanzas personales, educación financiera, inversión, ahorro, guías financieras',
      ogType: 'website',
    },
    en: {
      title: 'Personal Finance Resources & Articles | Finanzly',
      description: 'Guides, articles and educational resources about personal finance, investing, savings and taxes. Learn to manage your money better.',
      keywords: 'personal finance, financial education, investing, savings, financial guides',
      ogType: 'website',
    },
    fr: {
      title: 'Ressources et Articles de Finances Personnelles | Finanzly',
      description: 'Guides, articles et ressources éducatives sur les finances personnelles, investissement, épargne et impôts.',
      keywords: 'finances personnelles, éducation financière, investissement, épargne, guides financiers',
      ogType: 'website',
    },
  },
  
  about: {
    es: {
      title: 'Sobre Finanzly - Nuestra Misión | Finanzly',
      description: 'Conoce nuestra misión de democratizar la educación financiera mediante herramientas gratuitas y contenido de calidad para todos.',
      keywords: 'sobre finanzly, misión, educación financiera, herramientas financieras',
      ogType: 'website',
    },
    en: {
      title: 'About Finanzly - Our Mission | Finanzly',
      description: 'Learn about our mission to democratize financial education through free tools and quality content for everyone.',
      keywords: 'about finanzly, mission, financial education, financial tools',
      ogType: 'website',
    },
    fr: {
      title: 'À Propos de Finanzly - Notre Mission | Finanzly',
      description: 'Découvrez notre mission de démocratiser l\'éducation financière grâce à des outils gratuits et du contenu de qualité.',
      keywords: 'à propos de finanzly, mission, éducation financière, outils financiers',
      ogType: 'website',
    },
  },
  
  contact: {
    es: {
      title: 'Contacto - Habla con Nosotros | Finanzly',
      description: '¿Tienes preguntas o sugerencias? Contacta con el equipo de Finanzly. Estamos aquí para ayudarte con tus finanzas personales.',
      keywords: 'contacto finanzly, soporte, ayuda, preguntas',
      ogType: 'website',
    },
    en: {
      title: 'Contact - Get in Touch | Finanzly',
      description: 'Have questions or suggestions? Contact the Finanzly team. We\'re here to help with your personal finances.',
      keywords: 'contact finanzly, support, help, questions',
      ogType: 'website',
    },
    fr: {
      title: 'Contact - Contactez-nous | Finanzly',
      description: 'Vous avez des questions ou des suggestions ? Contactez l\'équipe de Finanzly. Nous sommes là pour vous aider.',
      keywords: 'contact finanzly, support, aide, questions',
      ogType: 'website',
    },
  },
  
  pricing: {
    es: {
      title: 'Precios y Planes - Premium | Finanzly',
      description: 'Descubre nuestros planes premium con herramientas avanzadas, análisis personalizados y soporte prioritario para gestionar mejor tus finanzas.',
      keywords: 'precios finanzly, planes premium, suscripción, herramientas premium',
      ogType: 'website',
    },
    en: {
      title: 'Pricing & Plans - Premium | Finanzly',
      description: 'Discover our premium plans with advanced tools, personalized analysis and priority support to better manage your finances.',
      keywords: 'finanzly pricing, premium plans, subscription, premium tools',
      ogType: 'website',
    },
    fr: {
      title: 'Tarifs et Plans - Premium | Finanzly',
      description: 'Découvrez nos plans premium avec des outils avancés, des analyses personnalisées et un support prioritaire.',
      keywords: 'tarifs finanzly, plans premium, abonnement, outils premium',
      ogType: 'website',
    },
  },
  
  chat: {
    es: {
      title: 'Chat de IA Financiera - Asistente Personal | Finanzly',
      description: 'Chatea con nuestro asistente de IA especializado en finanzas. Obtén respuestas instantáneas sobre inversión, ahorro y planificación financiera.',
      keywords: 'chat financiero, asistente ia, consejos financieros, chatbot finanzas',
      ogType: 'website',
    },
    en: {
      title: 'Financial AI Chat - Personal Assistant | Finanzly',
      description: 'Chat with our AI assistant specialized in finance. Get instant answers about investing, savings and financial planning.',
      keywords: 'financial chat, ai assistant, financial advice, finance chatbot',
      ogType: 'website',
    },
    fr: {
      title: 'Chat IA Financier - Assistant Personnel | Finanzly',
      description: 'Discutez avec notre assistant IA spécialisé en finances. Obtenez des réponses instantanées sur l\'investissement et l\'épargne.',
      keywords: 'chat financier, assistant ia, conseils financiers, chatbot finances',
      ogType: 'website',
    },
  },
  
  profile: {
    es: {
      title: 'Mi Perfil - Configuración de Cuenta | Finanzly',
      description: 'Gestiona tu perfil de Finanzly, ajusta tus preferencias y accede a tu historial de cálculos y recursos guardados.',
      keywords: 'perfil finanzly, configuración cuenta, mi cuenta',
      ogType: 'website',
      noindex: true, // User-specific pages should not be indexed
    },
    en: {
      title: 'My Profile - Account Settings | Finanzly',
      description: 'Manage your Finanzly profile, adjust your preferences and access your calculation history and saved resources.',
      keywords: 'finanzly profile, account settings, my account',
      ogType: 'website',
      noindex: true,
    },
    fr: {
      title: 'Mon Profil - Paramètres du Compte | Finanzly',
      description: 'Gérez votre profil Finanzly, ajustez vos préférences et accédez à votre historique de calculs.',
      keywords: 'profil finanzly, paramètres compte, mon compte',
      ogType: 'website',
      noindex: true,
    },
  },
};

/**
 * Legal Pages SEO Configuration
 */
export const LEGAL_PAGES_SEO: Record<string, MultiLangSEOMetadata> = {
  terms: {
    es: {
      title: 'Términos y Condiciones de Uso | Finanzly',
      description: 'Lee los términos y condiciones de uso de Finanzly. Conoce tus derechos y responsabilidades al usar nuestras herramientas financieras.',
      keywords: 'términos y condiciones, condiciones de uso, legal',
      ogType: 'website',
    },
    en: {
      title: 'Terms and Conditions of Use | Finanzly',
      description: 'Read Finanzly\'s terms and conditions of use. Know your rights and responsibilities when using our financial tools.',
      keywords: 'terms and conditions, terms of use, legal',
      ogType: 'website',
    },
    fr: {
      title: 'Conditions Générales d\'Utilisation | Finanzly',
      description: 'Lisez les conditions générales d\'utilisation de Finanzly. Connaissez vos droits et responsabilités.',
      keywords: 'conditions générales, conditions d\'utilisation, légal',
      ogType: 'website',
    },
  },
  
  privacy: {
    es: {
      title: 'Política de Privacidad | Finanzly',
      description: 'Conoce cómo Finanzly protege tu privacidad y gestiona tus datos personales. Tu información está segura con nosotros.',
      keywords: 'política de privacidad, protección datos, privacidad',
      ogType: 'website',
    },
    en: {
      title: 'Privacy Policy | Finanzly',
      description: 'Learn how Finanzly protects your privacy and manages your personal data. Your information is safe with us.',
      keywords: 'privacy policy, data protection, privacy',
      ogType: 'website',
    },
    fr: {
      title: 'Politique de Confidentialité | Finanzly',
      description: 'Découvrez comment Finanzly protège votre vie privée et gère vos données personnelles.',
      keywords: 'politique de confidentialité, protection des données, confidentialité',
      ogType: 'website',
    },
  },
  
  cookies: {
    es: {
      title: 'Política de Cookies | Finanzly',
      description: 'Información sobre el uso de cookies en Finanzly. Conoce qué cookies usamos y cómo puedes gestionarlas.',
      keywords: 'política cookies, cookies, gestión cookies',
      ogType: 'website',
    },
    en: {
      title: 'Cookie Policy | Finanzly',
      description: 'Information about cookie usage on Finanzly. Learn what cookies we use and how you can manage them.',
      keywords: 'cookie policy, cookies, cookie management',
      ogType: 'website',
    },
    fr: {
      title: 'Politique de Cookies | Finanzly',
      description: 'Informations sur l\'utilisation des cookies sur Finanzly. Découvrez quels cookies nous utilisons.',
      keywords: 'politique cookies, cookies, gestion cookies',
      ogType: 'website',
    },
  },
};

/**
 * Tool-specific SEO Configuration
 * Maps tool IDs to their metadata
 */
export const TOOLS_SEO: Record<string, MultiLangSEOMetadata> = {
  'mortgage-calculator': {
    es: {
      title: 'Calculadora de Hipotecas - Calcula tu Cuota Mensual | Finanzly',
      description: 'Calcula tu cuota hipotecaria mensual con nuestra calculadora gratuita. Compara tipos de interés, plazos y encuentra la mejor opción para tu hipoteca.',
      keywords: 'calculadora hipoteca, cuota mensual hipoteca, tipos de interés hipoteca, simulador hipoteca',
      ogType: 'website',
    },
    en: {
      title: 'Mortgage Calculator - Calculate Your Monthly Payment | Finanzly',
      description: 'Calculate your monthly mortgage payment with our free calculator. Compare interest rates, terms and find the best option.',
      keywords: 'mortgage calculator, monthly mortgage payment, mortgage interest rates, mortgage simulator',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice Hypothécaire - Calculez Votre Paiement Mensuel | Finanzly',
      description: 'Calculez votre paiement hypothécaire mensuel avec notre calculatrice gratuite. Comparez les taux d\'intérêt.',
      keywords: 'calculatrice hypothèque, paiement mensuel hypothèque, taux intérêt hypothèque',
      ogType: 'website',
    },
  },
  
  'monthly-savings-calculator': {
    es: {
      title: 'Calculadora de Ahorro Mensual - Planifica tu Futuro | Finanzly',
      description: 'Calcula cuánto puedes ahorrar mensualmente y ve cómo crece tu dinero con el interés compuesto. Planifica tu ahorro de forma inteligente.',
      keywords: 'calculadora ahorro mensual, ahorro, interés compuesto, planificación financiera',
      ogType: 'website',
    },
    en: {
      title: 'Monthly Savings Calculator - Plan Your Future | Finanzly',
      description: 'Calculate how much you can save monthly and see how your money grows with compound interest. Plan your savings smartly.',
      keywords: 'monthly savings calculator, savings, compound interest, financial planning',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice d\'Épargne Mensuelle - Planifiez Votre Avenir | Finanzly',
      description: 'Calculez combien vous pouvez économiser mensuellement et voyez comment votre argent grandit avec les intérêts composés.',
      keywords: 'calculatrice épargne mensuelle, épargne, intérêts composés, planification financière',
      ogType: 'website',
    },
  },
  
  'compound-interest-calculator': {
    es: {
      title: 'Calculadora de Interés Compuesto - Inversiones | Finanzly',
      description: 'Descubre el poder del interés compuesto. Calcula cuánto crecerá tu inversión a lo largo del tiempo con nuestra calculadora gratuita.',
      keywords: 'interés compuesto, calculadora inversión, rentabilidad inversión, crecimiento capital',
      ogType: 'website',
    },
    en: {
      title: 'Compound Interest Calculator - Investments | Finanzly',
      description: 'Discover the power of compound interest. Calculate how much your investment will grow over time with our free calculator.',
      keywords: 'compound interest, investment calculator, investment return, capital growth',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice d\'Intérêts Composés - Investissements | Finanzly',
      description: 'Découvrez la puissance des intérêts composés. Calculez la croissance de votre investissement avec notre calculatrice gratuite.',
      keywords: 'intérêts composés, calculatrice investissement, rendement investissement',
      ogType: 'website',
    },
  },
  
  'retirement-calculator': {
    es: {
      title: 'Calculadora de Jubilación - Planifica tu Retiro | Finanzly',
      description: 'Calcula cuánto necesitas ahorrar para jubilarte. Planifica tu jubilación y alcanza la libertad financiera con nuestra calculadora.',
      keywords: 'calculadora jubilación, planificación jubilación, ahorro jubilación, retiro',
      ogType: 'website',
    },
    en: {
      title: 'Retirement Calculator - Plan Your Retirement | Finanzly',
      description: 'Calculate how much you need to save for retirement. Plan your retirement and achieve financial freedom.',
      keywords: 'retirement calculator, retirement planning, retirement savings, retire',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice de Retraite - Planifiez Votre Retraite | Finanzly',
      description: 'Calculez combien vous devez économiser pour la retraite. Planifiez votre retraite et atteignez la liberté financière.',
      keywords: 'calculatrice retraite, planification retraite, épargne retraite',
      ogType: 'website',
    },
  },
  
  'emergency-fund-calculator': {
    es: {
      title: 'Calculadora de Fondo de Emergencia - Protege tu Futuro | Finanzly',
      description: 'Calcula cuánto necesitas en tu fondo de emergencia según tus gastos mensuales. Protégete ante imprevistos financieros.',
      keywords: 'fondo de emergencia, ahorro emergencia, colchón financiero, seguridad financiera',
      ogType: 'website',
    },
    en: {
      title: 'Emergency Fund Calculator - Protect Your Future | Finanzly',
      description: 'Calculate how much you need in your emergency fund based on your monthly expenses. Protect yourself from financial setbacks.',
      keywords: 'emergency fund, emergency savings, financial cushion, financial security',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice de Fonds d\'Urgence - Protégez Votre Avenir | Finanzly',
      description: 'Calculez combien vous avez besoin dans votre fonds d\'urgence selon vos dépenses mensuelles.',
      keywords: 'fonds d\'urgence, épargne urgence, coussin financier, sécurité financière',
      ogType: 'website',
    },
  },
  
  'investment-return-calculator': {
    es: {
      title: 'Calculadora de Rentabilidad de Inversión - ROI | Finanzly',
      description: 'Calcula la rentabilidad de tus inversiones (ROI). Analiza el rendimiento de tus inversiones y toma mejores decisiones financieras.',
      keywords: 'calculadora ROI, rentabilidad inversión, rendimiento inversión, retorno inversión',
      ogType: 'website',
    },
    en: {
      title: 'Investment Return Calculator - ROI | Finanzly',
      description: 'Calculate your investment returns (ROI). Analyze your investment performance and make better financial decisions.',
      keywords: 'ROI calculator, investment return, investment performance, return on investment',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice de Rendement d\'Investissement - ROI | Finanzly',
      description: 'Calculez le rendement de vos investissements (ROI). Analysez la performance de vos investissements.',
      keywords: 'calculatrice ROI, rendement investissement, performance investissement',
      ogType: 'website',
    },
  },
  
  'debt-payoff-calculator': {
    es: {
      title: 'Calculadora de Pago de Deudas - Libérate de Deudas | Finanzly',
      description: 'Crea un plan para pagar tus deudas más rápido. Compara métodos avalancha y bola de nieve con nuestra calculadora gratuita.',
      keywords: 'pagar deudas, calculadora deudas, método avalancha, método bola de nieve',
      ogType: 'website',
    },
    en: {
      title: 'Debt Payoff Calculator - Get Out of Debt | Finanzly',
      description: 'Create a plan to pay off your debts faster. Compare avalanche and snowball methods with our free calculator.',
      keywords: 'pay off debt, debt calculator, avalanche method, snowball method',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice de Remboursement de Dettes - Libérez-vous | Finanzly',
      description: 'Créez un plan pour rembourser vos dettes plus rapidement. Comparez les méthodes avalanche et boule de neige.',
      keywords: 'rembourser dettes, calculatrice dettes, méthode avalanche',
      ogType: 'website',
    },
  },
  
  'budget-calculator': {
    es: {
      title: 'Calculadora de Presupuesto - Controla tus Gastos | Finanzly',
      description: 'Crea y gestiona tu presupuesto mensual. Controla tus ingresos, gastos y ahorra más con nuestra calculadora de presupuesto gratuita.',
      keywords: 'calculadora presupuesto, presupuesto mensual, control gastos, gestión financiera',
      ogType: 'website',
    },
    en: {
      title: 'Budget Calculator - Control Your Spending | Finanzly',
      description: 'Create and manage your monthly budget. Track your income, expenses and save more with our free budget calculator.',
      keywords: 'budget calculator, monthly budget, expense tracking, financial management',
      ogType: 'website',
    },
    fr: {
      title: 'Calculatrice de Budget - Contrôlez Vos Dépenses | Finanzly',
      description: 'Créez et gérez votre budget mensuel. Suivez vos revenus, dépenses et économisez plus avec notre calculatrice gratuite.',
      keywords: 'calculatrice budget, budget mensuel, suivi dépenses, gestion financière',
      ogType: 'website',
    },
  },
};

/**
 * Article-specific SEO Configuration
 * Maps article IDs to their metadata
 */
export const ARTICLES_SEO: Record<string, MultiLangSEOMetadata> = {
  'couples-finance': {
    es: {
      title: 'Finanzas en Pareja - Guía Completa para Gestionar el Dinero | Finanzly',
      description: 'Aprende a gestionar las finanzas en pareja de forma efectiva. Consejos prácticos para manejar cuentas conjuntas, gastos y objetivos financieros compartidos.',
      keywords: 'finanzas en pareja, dinero en pareja, cuentas conjuntas, gestión financiera pareja',
      ogType: 'article',
    },
    en: {
      title: 'Couple\'s Finance - Complete Guide to Managing Money | Finanzly',
      description: 'Learn to manage couple finances effectively. Practical tips for handling joint accounts, expenses and shared financial goals.',
      keywords: 'couple finance, money in relationships, joint accounts, couple financial management',
      ogType: 'article',
    },
    fr: {
      title: 'Finances de Couple - Guide Complet pour Gérer l\'Argent | Finanzly',
      description: 'Apprenez à gérer les finances de couple efficacement. Conseils pratiques pour les comptes joints et objectifs financiers partagés.',
      keywords: 'finances de couple, argent en couple, comptes joints, gestion financière couple',
      ogType: 'article',
    },
  },
  
  'emergency-fund': {
    es: {
      title: 'Cómo Crear un Fondo de Emergencia - Guía Práctica | Finanzly',
      description: 'Guía completa para crear y mantener un fondo de emergencia. Descubre cuánto necesitas ahorrar y dónde guardar tu dinero.',
      keywords: 'fondo de emergencia, ahorro emergencia, colchón financiero, seguridad financiera',
      ogType: 'article',
    },
    en: {
      title: 'How to Build an Emergency Fund - Practical Guide | Finanzly',
      description: 'Complete guide to creating and maintaining an emergency fund. Discover how much you need to save and where to keep your money.',
      keywords: 'emergency fund, emergency savings, financial cushion, financial security',
      ogType: 'article',
    },
    fr: {
      title: 'Comment Créer un Fonds d\'Urgence - Guide Pratique | Finanzly',
      description: 'Guide complet pour créer et maintenir un fonds d\'urgence. Découvrez combien économiser et où garder votre argent.',
      keywords: 'fonds d\'urgence, épargne urgence, coussin financier, sécurité financière',
      ogType: 'article',
    },
  },
  
  'compound-interest': {
    es: {
      title: 'Qué es el Interés Compuesto y Cómo Funciona | Finanzly',
      description: 'Descubre el poder del interés compuesto y cómo puede multiplicar tus ahorros. La octava maravilla del mundo explicada de forma simple.',
      keywords: 'interés compuesto, inversión, rentabilidad, ahorro, crecimiento capital',
      ogType: 'article',
    },
    en: {
      title: 'What is Compound Interest and How Does it Work | Finanzly',
      description: 'Discover the power of compound interest and how it can multiply your savings. The eighth wonder of the world explained simply.',
      keywords: 'compound interest, investment, returns, savings, capital growth',
      ogType: 'article',
    },
    fr: {
      title: 'Qu\'est-ce que l\'Intérêt Composé et Comment Fonctionne-t-il | Finanzly',
      description: 'Découvrez la puissance des intérêts composés et comment ils peuvent multiplier votre épargne.',
      keywords: 'intérêt composé, investissement, rendements, épargne, croissance capital',
      ogType: 'article',
    },
  },
  
  'start-investing': {
    es: {
      title: 'Cómo Empezar a Invertir - Guía para Principiantes | Finanzly',
      description: 'Guía paso a paso para empezar a invertir desde cero. Aprende sobre ETFs, fondos indexados, diversificación y estrategias de inversión.',
      keywords: 'empezar a invertir, invertir desde cero, ETF, fondos indexados, inversión principiantes',
      ogType: 'article',
    },
    en: {
      title: 'How to Start Investing - Beginner\'s Guide | Finanzly',
      description: 'Step-by-step guide to start investing from scratch. Learn about ETFs, index funds, diversification and investment strategies.',
      keywords: 'start investing, invest from scratch, ETF, index funds, beginner investing',
      ogType: 'article',
    },
    fr: {
      title: 'Comment Commencer à Investir - Guide pour Débutants | Finanzly',
      description: 'Guide étape par étape pour commencer à investir de zéro. Apprenez sur les ETF, fonds indiciels et stratégies d\'investissement.',
      keywords: 'commencer investir, investir zéro, ETF, fonds indiciels, investissement débutants',
      ogType: 'article',
    },
  },
  
  'retirement-planning-article': {
    es: {
      title: 'Planificación de Jubilación - Guía Completa | Finanzly',
      description: 'Todo lo que necesitas saber sobre planificación de jubilación. Calcula cuánto necesitas ahorrar y las mejores estrategias para tu retiro.',
      keywords: 'planificación jubilación, ahorro jubilación, retiro, pensión, independencia financiera',
      ogType: 'article',
    },
    en: {
      title: 'Retirement Planning - Complete Guide | Finanzly',
      description: 'Everything you need to know about retirement planning. Calculate how much you need to save and the best strategies for your retirement.',
      keywords: 'retirement planning, retirement savings, retire, pension, financial independence',
      ogType: 'article',
    },
    fr: {
      title: 'Planification de la Retraite - Guide Complet | Finanzly',
      description: 'Tout ce que vous devez savoir sur la planification de la retraite. Calculez combien économiser et les meilleures stratégies.',
      keywords: 'planification retraite, épargne retraite, retraite, pension, indépendance financière',
      ogType: 'article',
    },
  },
  
  'inflation': {
    es: {
      title: 'Qué es la Inflación y Cómo Afecta tu Dinero | Finanzly',
      description: 'Entiende qué es la inflación, cómo se calcula y cómo proteger tus ahorros del aumento de precios. Guía completa sobre inflación.',
      keywords: 'inflación, poder adquisitivo, IPC, proteger ahorros, subida precios',
      ogType: 'article',
    },
    en: {
      title: 'What is Inflation and How Does it Affect Your Money | Finanzly',
      description: 'Understand what inflation is, how it\'s calculated and how to protect your savings from rising prices. Complete guide on inflation.',
      keywords: 'inflation, purchasing power, CPI, protect savings, price increases',
      ogType: 'article',
    },
    fr: {
      title: 'Qu\'est-ce que l\'Inflation et Comment Affecte-t-elle Votre Argent | Finanzly',
      description: 'Comprenez ce qu\'est l\'inflation, comment elle est calculée et comment protéger votre épargne de la hausse des prix.',
      keywords: 'inflation, pouvoir achat, IPC, protéger épargne, hausse prix',
      ogType: 'article',
    },
  },
  
  'etf-index-funds': {
    es: {
      title: 'ETFs y Fondos Indexados - Guía Completa de Inversión | Finanzly',
      description: 'Todo sobre ETFs y fondos indexados: qué son, cómo funcionan, ventajas, diferencias y cómo elegir los mejores para tu cartera.',
      keywords: 'ETF, fondos indexados, inversión pasiva, diversificación, índices bursátiles',
      ogType: 'article',
    },
    en: {
      title: 'ETFs and Index Funds - Complete Investment Guide | Finanzly',
      description: 'Everything about ETFs and index funds: what they are, how they work, advantages, differences and how to choose the best for your portfolio.',
      keywords: 'ETF, index funds, passive investing, diversification, stock indices',
      ogType: 'article',
    },
    fr: {
      title: 'ETF et Fonds Indiciels - Guide Complet d\'Investissement | Finanzly',
      description: 'Tout sur les ETF et fonds indiciels : ce qu\'ils sont, comment ils fonctionnent, avantages et comment choisir les meilleurs.',
      keywords: 'ETF, fonds indiciels, investissement passif, diversification, indices boursiers',
      ogType: 'article',
    },
  },
  
  'crypto-tax-guide': {
    es: {
      title: 'Guía de Impuestos sobre Criptomonedas en España | Finanzly',
      description: 'Guía completa sobre la tributación de criptomonedas en España. Aprende cómo declarar Bitcoin, Ethereum y otras criptos en Hacienda.',
      keywords: 'impuestos criptomonedas, declarar bitcoin, hacienda criptomonedas, tributación cripto',
      ogType: 'article',
    },
    en: {
      title: 'Cryptocurrency Tax Guide in Spain | Finanzly',
      description: 'Complete guide on cryptocurrency taxation in Spain. Learn how to declare Bitcoin, Ethereum and other cryptos to the tax authority.',
      keywords: 'cryptocurrency taxes, declare bitcoin, crypto taxation, crypto tax guide',
      ogType: 'article',
    },
    fr: {
      title: 'Guide Fiscal des Cryptomonnaies en Espagne | Finanzly',
      description: 'Guide complet sur la fiscalité des cryptomonnaies en Espagne. Apprenez à déclarer Bitcoin, Ethereum et autres cryptos.',
      keywords: 'impôts cryptomonnaies, déclarer bitcoin, fiscalité crypto',
      ogType: 'article',
    },
  },
  
  'digital-nomad-tax-guide': {
    es: {
      title: 'Guía Fiscal para Nómadas Digitales en España | Finanzly',
      description: 'Todo lo que necesitas saber sobre impuestos como nómada digital en España. Residencia fiscal, deducciones y obligaciones tributarias.',
      keywords: 'nómada digital impuestos, residencia fiscal, autónomo digital, teletrabajo internacional',
      ogType: 'article',
    },
    en: {
      title: 'Tax Guide for Digital Nomads in Spain | Finanzly',
      description: 'Everything you need to know about taxes as a digital nomad in Spain. Tax residency, deductions and tax obligations.',
      keywords: 'digital nomad taxes, tax residency, digital freelancer, international remote work',
      ogType: 'article',
    },
    fr: {
      title: 'Guide Fiscal pour Nomades Numériques en Espagne | Finanzly',
      description: 'Tout ce que vous devez savoir sur les impôts en tant que nomade numérique en Espagne. Résidence fiscale et obligations.',
      keywords: 'nomade numérique impôts, résidence fiscale, freelance digital',
      ogType: 'article',
    },
  },
  
  'automate-finances': {
    es: {
      title: 'Cómo Automatizar tus Finanzas Personales | Finanzly',
      description: 'Guía práctica para automatizar tus finanzas. Configura transferencias automáticas, ahorro e inversiones sin esfuerzo.',
      keywords: 'automatizar finanzas, ahorro automático, transferencias automáticas, finanzas automatizadas',
      ogType: 'article',
    },
    en: {
      title: 'How to Automate Your Personal Finances | Finanzly',
      description: 'Practical guide to automate your finances. Set up automatic transfers, savings and investments effortlessly.',
      keywords: 'automate finances, automatic savings, automatic transfers, automated finances',
      ogType: 'article',
    },
    fr: {
      title: 'Comment Automatiser Vos Finances Personnelles | Finanzly',
      description: 'Guide pratique pour automatiser vos finances. Configurez des virements automatiques, épargne et investissements sans effort.',
      keywords: 'automatiser finances, épargne automatique, virements automatiques',
      ogType: 'article',
    },
  },
};

/**
 * Get SEO metadata for a route
 * @param type - Type of page (main, legal, tool, article)
 * @param id - Page or resource identifier
 * @param lang - Language code
 * @returns SEO metadata or undefined if not found
 */
export function getSEOMetadata(
  type: 'main' | 'legal' | 'tool' | 'article',
  id: string,
  lang: SupportedLanguage
): SEOMetadata | undefined {
  let config: Record<string, MultiLangSEOMetadata>;
  
  switch (type) {
    case 'main':
      config = MAIN_PAGES_SEO;
      break;
    case 'legal':
      config = LEGAL_PAGES_SEO;
      break;
    case 'tool':
      config = TOOLS_SEO;
      break;
    case 'article':
      config = ARTICLES_SEO;
      break;
    default:
      return undefined;
  }
  
  return config[id]?.[lang];
}

/**
 * Generate a canonical URL for a given path
 * @param path - Path relative to root (e.g., '/es/tools')
 * @returns Full canonical URL
 */
export function getCanonicalURL(path: string): string {
  // Remove trailing slash if present
  const cleanPath = path.endsWith('/') && path.length > 1 
    ? path.slice(0, -1) 
    : path;
  
  return `${SEO_DOMAIN}${cleanPath}`;
}
